import { type DSDReportListItemResponse } from "@/apis/dsd-editor/route";
import { logUserAction } from "@/apis/logs/route";
import { useUserNames } from "@/hooks/queries/use-user-names";
import {
  isDsdReviewFailedModalOpen,
  openDsdReviewFailedModal,
  openDsdReviewView,
  useStartDsdReview,
} from "@/hooks/mutations/use-start-dsd-review";
import queries from "@/queries";
import {
  formatUploadedAt,
  getReportMetaLabel,
  getReportPageWindow,
} from "@/utils/dsd-editor/report-list";
import { subscribeDsdEditorReviewLoadError } from "@/utils/dsd-editor/review-load-error-channel";
import {
  DSD_EDITOR_MODE,
  type DsdEditorMode,
  type DsdReviewLocationState,
} from "@/types/dsd";
import { getUserDisplayName } from "@/utils/user-display-name";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DSD_EDITOR_COPY } from "./dsd-editor-copy";
import {
  DsdEditorActionSection,
  DsdEditorExistingReportActionButton,
  DsdEditorExistingReportPicker,
  DsdEditorModeSwitch,
  DsdEditorPageHeader,
  DsdEditorPageShell,
  DsdEditorPagination,
  DsdEditorUploadActionButton,
  DsdEditorUploadFileSection,
} from "./dsd-editor-page-elements";

const DSD_REPORT_PAGE_SIZE = 20;
const DSD_REPORT_QUERY_LIMIT = DSD_REPORT_PAGE_SIZE + 1;

const isDsdEditorMode = (value: unknown): value is DsdEditorMode =>
  Object.values(DSD_EDITOR_MODE).some((mode) => mode === value);

export const DsdReviewPage = () => {
  // history.state 는 외부 입력이다 — 옛 배포가 남긴 값이 섞여도 업로드 모드로 돌려보낸다.
  const stateMode = (useLocation().state as DsdReviewLocationState)?.mode;
  const [dsdFile, setDsdFile] = useState<File | null>(null);
  const [mode, setMode] = useState<DsdEditorMode>(
    isDsdEditorMode(stateMode) ? stateMode : DSD_EDITOR_MODE.upload,
  );
  const [selectedDsdReport, setSelectedDsdReport] =
    useState<DSDReportListItemResponse | null>(null);
  const selectedDsdReportId = selectedDsdReport?.reportId ?? null;
  const [reportPage, setReportPage] = useState(0);
  const reportOffset = reportPage * DSD_REPORT_PAGE_SIZE;

  useEffect(
    () =>
      subscribeDsdEditorReviewLoadError(() => {
        openDsdReviewFailedModal();
      }),
    [],
  );

  const reportsQuery = useQuery({
    ...queries.dsdEditorReports({
      limit: DSD_REPORT_QUERY_LIMIT,
      offset: reportOffset,
    }),
    enabled: mode === DSD_EDITOR_MODE.existingReport,
  });

  const { visibleReports, hasNextReportPage } = useMemo(
    () =>
      getReportPageWindow(
        reportsQuery.data?.reports ?? [],
        DSD_REPORT_PAGE_SIZE,
      ),
    [reportsQuery.data?.reports],
  );
  const uploaderEmails = useMemo(
    () => visibleReports.map((report) => report.uploaderEmail),
    [visibleReports],
  );
  const { data: userNamesMap } = useUserNames(uploaderEmails);

  const review = useStartDsdReview();
  const { reset: resetUploadMutation } = review;

  const handleFileSelect = useCallback(
    (file: File) => {
      setDsdFile(file);
      resetUploadMutation();
    },
    [resetUploadMutation],
  );

  const handleUpload = () => {
    if (dsdFile) review.start(dsdFile);
  };

  const handleOpenSelectedReport = () => {
    if (!selectedDsdReport) return;

    // reportId 가 곧 업로드 파일 id 라, 그대로 넘기면 백엔드가 링크를 만든다.
    logUserAction("DSD 리뷰 시작", [
      {
        filename: selectedDsdReport.filename,
        dsdFileId: selectedDsdReport.reportId,
      },
    ]);
    openDsdReviewView(selectedDsdReport.reportId);
  };

  // 리뷰 시작 버튼이 활성화된 상태면 Enter로 바로 시작.
  // 리스너는 한 번만 등록하고, 최신 mode/핸들러는 ref로 참조.
  const enterActionRef = useRef<() => void>(() => {});
  enterActionRef.current =
    mode === DSD_EDITOR_MODE.upload ? handleUpload : handleOpenSelectedReport;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (event.isComposing || event.keyCode === 229) return; // IME 조합 중
      if (isDsdReviewFailedModalOpen()) return;

      const target = event.target as HTMLElement | null;
      const interactive = target?.closest(
        "button, a, input, textarea, select, [role='button'], [contenteditable='true']",
      );

      // 리포트 항목 버튼은 예외: Enter로 선택 토글(해제) 대신 리뷰 시작.
      // 그 외 인터랙티브 요소는 자체 Enter 동작과 중복되므로 무시.
      if (interactive) {
        if (!interactive.closest("[data-report-option]")) return;
        event.preventDefault();
      }

      enterActionRef.current();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <DsdEditorPageShell>
      <DsdEditorPageHeader />
      <DsdEditorModeSwitch value={mode} onChange={setMode} />

      {mode === DSD_EDITOR_MODE.upload ? (
        <DsdEditorActionSection
          action={
            <DsdEditorUploadActionButton
              isPending={review.isPending}
              disabled={!dsdFile}
              onClick={handleUpload}
            />
          }
        >
          <DsdEditorUploadFileSection
            onFileSelect={handleFileSelect}
            isLoading={review.isPending}
            fileName={dsdFile?.name}
          />

          {review.isError && (
            <p className="caption-2-medium text-status-negative">
              {DSD_EDITOR_COPY.sources.upload.errorMessage}
            </p>
          )}
        </DsdEditorActionSection>
      ) : (
        <DsdEditorActionSection
          action={
            <DsdEditorExistingReportActionButton
              disabled={!selectedDsdReportId}
              onClick={handleOpenSelectedReport}
            />
          }
        >
          <DsdEditorExistingReportPicker
            reports={visibleReports}
            selectedReportId={selectedDsdReportId}
            onSelect={(_reportId, report) => setSelectedDsdReport(report)}
            renderReportMeta={(report) => {
              const metaLabel = getReportMetaLabel(report);
              const uploaderLabel = getUserDisplayName(
                userNamesMap,
                report.uploaderEmail,
              );

              return (
                <span className="flex flex-wrap gap-x-2 gap-y-1">
                  {metaLabel && <span>{metaLabel}</span>}
                  {metaLabel && <span aria-hidden="true">·</span>}
                  <span>
                    {
                      DSD_EDITOR_COPY.sources.existingReport.meta
                        .uploadedAtPrefix
                    }{" "}
                    {formatUploadedAt(report.uploadedAt)}
                  </span>
                  {uploaderLabel && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{uploaderLabel}</span>
                    </>
                  )}
                </span>
              );
            }}
            isLoading={reportsQuery.isLoading}
            isError={reportsQuery.isError}
            onRetry={() => reportsQuery.refetch()}
          />

          {!reportsQuery.isLoading &&
            !reportsQuery.isError &&
            (reportPage > 0 || hasNextReportPage) && (
              <DsdEditorPagination
                page={reportPage}
                hasNextPage={hasNextReportPage}
                disabled={reportsQuery.isFetching}
                onPrevious={() =>
                  setReportPage((current) => Math.max(current - 1, 0))
                }
                onNext={() => setReportPage((current) => current + 1)}
              />
            )}
        </DsdEditorActionSection>
      )}
    </DsdEditorPageShell>
  );
};
